import {
    Sparkles,
    FileSearch,
    Workflow,
    ScanText,
    LineChart,
    Blocks,
    Landmark,
    Boxes,
    Factory,
    Users,
    BarChart3,
    GitPullRequestArrow,
    MonitorPlay,
    PenTool,
    Wifi,
    AppWindow,
    Video,
    GraduationCap,
    Sun,
    PlugZap,
    BatteryCharging,
    Activity,
    Wind,
    BadgeCheck,
    Building2,
    HeartPulse,
    Store,
    Briefcase,
    Presentation,
    Home,
    Building,
} from "lucide-react";

export const PRODUCTS = [
    {
        slug: "ai-web-based-software",
        name: "AI Web Based Software",
        tagline: "Intelligent web applications, crafted around the way you work.",
        positioning:
            "Custom AI-powered platforms that automate the busywork and surface the answers.",
        overview:
            "We design and build browser-based business software with an AI layer baked in from the first sprint — client portals, coaching dashboards, booking systems and internal tools that draft, summarise, extract and predict. Every build is shaped around your workflow, deploys to the cloud or your own servers, and ships with training included.",
        image: "/assets/product-ai-software.jpeg",
        imageAlt: "AI-powered web software dashboard with intelligent insights",
        icon: Sparkles,
        glance: [
            { value: "100%", label: "Browser-based" },
            { value: "<200 ms", label: "Median response" },
            { value: "10,000", label: "Concurrent users" },
            { value: "99.9%", label: "Uptime SLA" },
            { value: "15+", label: "Languages" },
        ],
        features: [
            {
                icon: Sparkles,
                title: "Built-in LLM assistant",
                text: "Ask questions, draft content and summarise records from any screen — the assistant knows your data.",
            },
            {
                icon: FileSearch,
                title: "Document Q&A with RAG",
                text: "Point it at your policies, contracts and knowledge base, and get cited answers in seconds.",
            },
            {
                icon: Workflow,
                title: "Workflow automation",
                text: "Approvals, reminders and escalations run themselves — configured visually, no code required.",
            },
            {
                icon: ScanText,
                title: "OCR & data extraction",
                text: "Invoices, forms and ID documents become clean, structured data the moment they arrive.",
            },
            {
                icon: LineChart,
                title: "Predictive analytics",
                text: "Forecasts, anomaly detection and natural-language reporting turn raw records into decisions.",
            },
            {
                icon: Blocks,
                title: "Open API & SSO",
                text: "REST and GraphQL APIs, webhooks and enterprise single sign-on connect it to everything else.",
            },
        ],
        benefits: [
            { stat: "12 hrs", text: "of manual work removed per team, every single week" },
            { stat: "Seconds", text: "to answers, with natural-language reporting across your data" },
            { stat: "<200 ms", text: "median response time keeps fast-moving teams in flow" },
            { stat: "Day one", text: "onboarding and data migration included with every plan" },
        ],
        audiences: [
            {
                icon: Building2,
                title: "MSMEs & growing businesses",
                text: "Replace spreadsheet sprawl with one intelligent system that scales as you hire.",
            },
            {
                icon: Landmark,
                title: "Enterprises",
                text: "Single-tenant or on-premise deployments with SSO, audit logs and 24x7 support.",
            },
            {
                icon: GraduationCap,
                title: "Schools & institutions",
                text: "Admissions, communication and reporting — automated in 15+ languages.",
            },
            {
                icon: HeartPulse,
                title: "Healthcare & services",
                text: "Appointments, records and billing with privacy controls built in.",
            },
        ],
        specGroups: [
            {
                title: "Platform & Deployment",
                rows: [
                    ["Access", "100% browser-based; installable PWA"],
                    ["Hosting", "Cloud (AWS / Azure / GCP) or on-premise; multi-tenant SaaS or single-tenant"],
                    ["Browsers", "Chrome, Edge, Safari, Firefox (latest 2 versions)"],
                    ["Devices", "Responsive on desktop, tablet and mobile"],
                ],
            },
            {
                title: "AI Capabilities",
                rows: [
                    ["Assistant", "Built-in LLM assistant across the application"],
                    ["Document intelligence", "Document Q&A with RAG; OCR and data extraction"],
                    ["Analytics", "Predictive analytics, anomaly detection, natural-language reporting"],
                    ["Languages", "15+ languages"],
                    ["Privacy", "Private / on-premise inference option"],
                ],
            },
            {
                title: "Core Modules",
                rows: [
                    ["Included modules", "CRM, workflow automation, billing & invoicing, HRMS, helpdesk, document management, custom form builder"],
                    ["API", "Open REST / GraphQL API"],
                ],
            },
            {
                title: "Integrations",
                rows: [
                    ["Business tools", "Tally, Zoho, Google Workspace, Microsoft 365"],
                    ["Messaging & payments", "WhatsApp Business, Razorpay, Stripe"],
                    ["Automation", "Zapier, webhooks"],
                    ["Identity (SSO)", "SAML 2.0, OAuth 2.0, LDAP"],
                ],
            },
            {
                title: "Security & Compliance",
                rows: [
                    ["Encryption", "AES-256 at rest, TLS 1.3 in transit"],
                    ["Access control", "RBAC, MFA, audit logs, IP allow-listing"],
                    ["Data protection", "Daily automated backups; GDPR & DPDP-ready"],
                    ["Reliability", "99.9% uptime SLA; ISO 27001-aligned processes"],
                ],
            },
            {
                title: "Performance",
                rows: [
                    ["Concurrency", "Up to 10,000 concurrent users"],
                    ["API rate", "1,000 requests/min"],
                    ["Response time", "Median under 200 ms"],
                    ["File upload", "Up to 2 GB per file"],
                ],
            },
            {
                title: "Licensing & Support",
                rows: [
                    ["Licensing", "Per-user/month or perpetual licence; 14-day free trial"],
                    ["Support", "Email, chat & phone; 24x7 for enterprise"],
                    ["Onboarding", "Onboarding and data migration included"],
                ],
            },
            {
                title: "Training",
                rows: [
                    ["Enablement", "Role-based training sessions; admin certification"],
                    ["Updates", "Quarterly release updates"],
                ],
            },
        ],
        delivery: [
            { step: "Discover", text: "We map your workflows and agree the outcomes that matter." },
            { step: "Configure", text: "Modules are configured and the AI is trained on your documents." },
            { step: "Migrate", text: "Your data moves in, your team signs off UAT." },
            { step: "Launch", text: "Go-live with role-based training and quarterly updates." },
        ],
        faqs: [
            {
                q: "Is it really 100% browser-based?",
                a: "Yes. There is nothing to install — your team signs in from Chrome, Edge, Safari or Firefox, on desktop, tablet or mobile. It can also be installed as a PWA for an app-like experience.",
            },
            {
                q: "Can our data stay on our own servers?",
                a: "Yes. Alongside cloud hosting on AWS, Azure or GCP, we offer on-premise deployment with private AI inference, so sensitive data never leaves your environment.",
            },
            {
                q: "How does the AI learn our business?",
                a: "Through retrieval-augmented generation: we connect your documents, knowledge base and records, and the assistant answers with citations from your own material — never invented facts.",
            },
            {
                q: "What happens after the 14-day free trial?",
                a: "You choose per-user monthly licensing or a perpetual licence. Everything you built during the trial carries over, and onboarding plus data migration are included either way.",
            },
            {
                q: "Does it connect with Tally, Google Workspace or Microsoft 365?",
                a: "Yes — those integrations ship out of the box, along with WhatsApp Business, Razorpay, Stripe, Zapier, webhooks and SSO via SAML 2.0, OAuth 2.0 or LDAP.",
            },
        ],
    },
    {
        slug: "erp-solution",
        name: "ERP Solution",
        tagline: "Every department. One source of truth.",
        positioning:
            "A modular ERP that runs finance, inventory, manufacturing and HR — GST-ready from day one.",
        overview:
            "Our ERP connects finance, inventory, sales, purchase, manufacturing, HR and projects into a single real-time system. It is built for Indian compliance — GST, e-Invoicing, e-Way bills and TDS are native, not bolted on — and deploys to the cloud, your own servers, or a hybrid of both, with Android and iOS apps for the shop floor.",
        image: "/assets/product-erp.jpeg",
        imageAlt: "ERP solution dashboards covering finance, inventory and operations",
        icon: Boxes,
        glance: [
            { value: "10+", label: "Integrated modules" },
            { value: "1M+", label: "Transactions / month" },
            { value: "500", label: "Concurrent users" },
            { value: "4–12 wks", label: "Typical go-live" },
            { value: "GST", label: "e-Invoice & e-Way ready" },
        ],
        features: [
            {
                icon: Landmark,
                title: "Finance & accounting",
                text: "GL, AP/AR, GST and e-invoicing, TDS, multi-currency and multi-company books.",
            },
            {
                icon: Boxes,
                title: "Inventory & warehouse",
                text: "Multi-location stock with batch, serial and expiry tracking, barcode and QR, and reorder automation.",
            },
            {
                icon: Factory,
                title: "Manufacturing",
                text: "BOM, work orders, production planning and job work — with offline shop-floor entry that syncs itself.",
            },
            {
                icon: Users,
                title: "HR & payroll",
                text: "Attendance, shifts, PF/ESI and payslips, tied directly into costing and accounts.",
            },
            {
                icon: BarChart3,
                title: "BI dashboards",
                text: "Role-based, real-time dashboards show every head exactly the numbers they own.",
            },
            {
                icon: GitPullRequestArrow,
                title: "Approvals & workflow",
                text: "Purchase, discount and payment approvals route themselves with a full audit trail.",
            },
        ],
        benefits: [
            { stat: "One", text: "source of truth from purchase order to payment" },
            { stat: "4–12 wks", text: "typical go-live, followed by 3 months of hypercare" },
            { stat: "100%", text: "GST, e-Invoice and e-Way bill coverage built in" },
            { stat: "Zero", text: "stock surprises — inventory is live across every location" },
        ],
        audiences: [
            {
                icon: Factory,
                title: "Manufacturing",
                text: "BOM to dispatch with production planning, job work and shop-floor capture.",
            },
            {
                icon: Store,
                title: "Retail & distribution",
                text: "Multi-location stock, barcode billing and reorder automation.",
            },
            {
                icon: Building2,
                title: "Multi-company enterprises",
                text: "Multi-company, multi-currency books with consolidated reporting.",
            },
            {
                icon: GraduationCap,
                title: "Educational institutions",
                text: "Fees, payroll, assets and stores on one compliant system.",
            },
        ],
        specGroups: [
            {
                title: "Modules",
                rows: [
                    ["Finance & accounting", "GL, AP/AR, GST and e-invoicing, TDS, multi-currency, multi-company"],
                    ["Inventory & warehouse", "Multi-location, batch/serial/expiry, barcode and QR, reorder automation"],
                    ["Sales & purchase", "Sales and CRM; purchase and vendor management"],
                    ["Manufacturing", "BOM, work orders, production planning, job work"],
                    ["HR & payroll", "Attendance, shifts, PF/ESI, payslips"],
                    ["Also included", "Project and costing, fixed assets, approvals and workflow, BI dashboards"],
                ],
            },
            {
                title: "Deployment",
                rows: [
                    ["Models", "Cloud SaaS, on-premise or hybrid"],
                    ["Server", "Windows or Linux server; PostgreSQL 14+ or SQL Server 2019+"],
                    ["Mobile", "Android and iOS apps; offline shop-floor entry with auto-sync"],
                ],
            },
            {
                title: "Compliance",
                rows: [
                    ["Tax (India)", "GST, e-Way bill, e-Invoice, TDS/TCS, multi-GSTIN"],
                    ["Controls", "Audit trail, role-based access control"],
                    ["Readiness", "MSME-ready"],
                ],
            },
            {
                title: "Integrations",
                rows: [
                    ["Migration", "Tally migration; Excel/CSV import-export"],
                    ["Commerce & logistics", "Shopify, WooCommerce, logistics APIs"],
                    ["Payments & banking", "Payment gateways; bank statement import"],
                    ["Connectivity", "REST API, SSO"],
                ],
            },
            {
                title: "Performance",
                rows: [
                    ["Volume", "1M+ transactions per month"],
                    ["Concurrency", "500 concurrent users"],
                    ["Uptime", "99.9%"],
                    ["Backup", "Incremental daily backup"],
                ],
            },
            {
                title: "Implementation",
                rows: [
                    ["Process", "Business study, data migration, configuration, UAT, go-live"],
                    ["Hypercare", "3 months post go-live"],
                    ["Timeline", "Typical go-live in 4–12 weeks"],
                ],
            },
            {
                title: "Licensing",
                rows: [
                    ["Licences", "Named or concurrent user licences; module-wise purchase"],
                    ["Maintenance", "Annual maintenance contract"],
                    ["Enablement", "Training and documentation included"],
                ],
            },
        ],
        delivery: [
            { step: "Study", text: "A business study maps your processes and compliance needs." },
            { step: "Migrate", text: "Tally and Excel data moves in cleanly, balances verified." },
            { step: "Configure", text: "Modules configured, workflows set, UAT signed off." },
            { step: "Go live", text: "Launch with 3 months of hypercare and an AMC behind it." },
        ],
        faqs: [
            {
                q: "How long does implementation actually take?",
                a: "A typical go-live is 4–12 weeks depending on modules and data volume: business study, migration, configuration, UAT, then go-live with 3 months of hypercare.",
            },
            {
                q: "Can you migrate our data from Tally?",
                a: "Yes — Tally migration is a standard part of implementation, and Excel/CSV import-export covers everything else. Opening balances are verified with your accountant before go-live.",
            },
            {
                q: "Is GST compliance really built in?",
                a: "Fully: GST returns, e-Invoicing, e-Way bills, TDS/TCS and multi-GSTIN are native features with a complete audit trail — no third-party plugins needed.",
            },
            {
                q: "Cloud or on-premise — which should we choose?",
                a: "Both are first-class. Cloud SaaS suits most teams; on-premise or hybrid suits factories with patchy connectivity. Offline shop-floor entry syncs automatically either way.",
            },
            {
                q: "What happens after go-live?",
                a: "Three months of hypercare are included, then an annual maintenance contract keeps updates, support and training rolling.",
            },
        ],
    },
    {
        slug: "interactive-panel",
        name: "Interactive Panel",
        tagline: "Meetings and classrooms that respond to touch.",
        positioning:
            "4K interactive flat panels that turn any room into a collaborative studio.",
        overview:
            "Our interactive panels combine a 4K UHD anti-glare display, up to 40-point touch and a built-in Android whiteboard in sizes from 65 to 98 inches. Wireless sharing, lesson recording and an optional Windows OPS PC make them equally at home in classrooms, coaching studios and boardrooms — backed by a 3-year on-site warranty and pan-India service.",
        image: "/assets/product-panel.jpeg",
        imageAlt: "Interactive touch panel in use in a modern classroom",
        icon: Presentation,
        glance: [
            { value: "4K UHD", label: "Up to 98″" },
            { value: "40-pt", label: "Multi-touch" },
            { value: "8 ms", label: "Touch response" },
            { value: "3 yrs", label: "On-site warranty" },
            { value: "×4", label: "Wireless screen share" },
        ],
        features: [
            {
                icon: MonitorPlay,
                title: "4K UHD anti-glare display",
                text: "Zero-bonded tempered glass with AG+AF coating stays readable from every seat in the room.",
            },
            {
                icon: PenTool,
                title: "40-point touch, dual-pen",
                text: "2–3 mm accuracy and 8 ms response with palm rejection — two people can write at once.",
            },
            {
                icon: Wifi,
                title: "4-device wireless sharing",
                text: "Android, iOS and Windows devices cast to the panel simultaneously, no dongles required.",
            },
            {
                icon: AppWindow,
                title: "Built-in Android whiteboard",
                text: "Octa-core Android board with whiteboard, split screen, file manager and screen recorder built in.",
            },
            {
                icon: Video,
                title: "Room-ready AV",
                text: "Front-facing speakers, an 8-array microphone and an optional 4K camera cover hybrid sessions.",
            },
            {
                icon: GraduationCap,
                title: "Classroom & meeting software",
                text: "Subject tools, lesson recording with cloud upload and classroom management included free.",
            },
        ],
        benefits: [
            { stat: "8 ms", text: "touch response — digital ink keeps up with the fastest ideas" },
            { stat: "×4", text: "devices share the screen at the same time" },
            { stat: "50,000 hrs", text: "panel life (MTBF) for years of daily use" },
            { stat: "3 yrs", text: "on-site warranty with a pan-India service network" },
        ],
        audiences: [
            {
                icon: GraduationCap,
                title: "Schools & colleges",
                text: "Interactive lessons, subject tools and recording that teachers actually use.",
            },
            {
                icon: Briefcase,
                title: "Corporate boardrooms",
                text: "Wireless presenting, annotation over any app and hybrid-meeting AV.",
            },
            {
                icon: Presentation,
                title: "Training & coaching studios",
                text: "Run cohorts with live whiteboarding and session recording.",
            },
            {
                icon: Landmark,
                title: "Government & public sector",
                text: "Durable panels with on-site training and nationwide service cover.",
            },
        ],
        specGroups: [
            {
                title: "Display",
                rows: [
                    ["Sizes", "65″ / 75″ / 86″ / 98″ 4K UHD (3840×2160)"],
                    ["Brightness", "400–500 nits; 4000:1 contrast"],
                    ["Viewing", "178° viewing angle"],
                    ["Glass", "Anti-glare tempered glass, zero-bonding, AG+AF coating"],
                ],
            },
            {
                title: "Touch",
                rows: [
                    ["Multi-touch", "20/40-point (IR or capacitive)"],
                    ["Precision", "2–3 mm accuracy; 8 ms response"],
                    ["Writing", "Dual-pen writing with palm rejection"],
                    ["Durability", "50,000-hour MTBF"],
                ],
            },
            {
                title: "Built-in Android Board",
                rows: [
                    ["Processor", "Octa-core CPU"],
                    ["Memory", "4/8 GB RAM; 32/64/128 GB storage"],
                    ["OS", "Android 11/13"],
                    ["Apps", "Built-in whiteboard, file manager, split screen, screen recorder"],
                ],
            },
            {
                title: "OPS PC Slot",
                rows: [
                    ["Processor", "Intel i3/i5/i7"],
                    ["Memory", "Up to 16 GB RAM; 256/512 GB SSD"],
                    ["OS", "Windows 11/10 (optional)"],
                ],
            },
            {
                title: "Connectivity",
                rows: [
                    ["Video", "HDMI in ×2–3, HDMI out, VGA"],
                    ["USB", "USB 2.0/3.0, USB Type-C"],
                    ["Network", "RJ45, Wi-Fi 5/6 dual band, Bluetooth 5.0"],
                    ["Control & audio", "RS232, audio out, mic in"],
                ],
            },
            {
                title: "Audio & Camera",
                rows: [
                    ["Speakers", "2×15 W or 2×20 W front speakers"],
                    ["Microphone", "8-array microphone"],
                    ["Camera", "4K camera option"],
                ],
            },
            {
                title: "Software",
                rows: [
                    ["Whiteboard", "Free interactive whiteboard; annotation over any app"],
                    ["Sharing", "Wireless screen share for up to 4 devices (Android/iOS/Windows)"],
                    ["Classroom", "Subject tools, lesson recording with cloud upload, classroom management"],
                ],
            },
            {
                title: "Installation & Warranty",
                rows: [
                    ["Mounting", "Wall mount or mobile stand"],
                    ["Warranty", "3-year on-site warranty (extendable)"],
                    ["Service", "Pan-India service network; on-site teacher training"],
                ],
            },
            {
                title: "Power",
                rows: [
                    ["Input", "AC 100–240 V"],
                    ["Consumption", "Approx. 250–400 W typical; ECO mode"],
                ],
            },
        ],
        delivery: [
            { step: "Survey", text: "We measure the room and recommend the right size and mount." },
            { step: "Install", text: "Wall mount or mobile stand, installed and levelled by our crew." },
            { step: "Train", text: "On-site training gets teachers and teams fluent in one session." },
            { step: "Support", text: "Pan-India service network with a 3-year on-site warranty." },
        ],
        faqs: [
            {
                q: "Which size should we choose?",
                a: "65″ suits standard classrooms, 75″ and 86″ fit larger classrooms and boardrooms, and 98″ is built for seminar halls. We confirm the right size during a free site survey.",
            },
            {
                q: "Does the panel need a separate computer?",
                a: "No — the built-in Android board runs the whiteboard, browser, file manager and recorder on its own. If you need Windows, an optional OPS PC (up to Intel i7) slots straight in.",
            },
            {
                q: "Can participants share their screens wirelessly?",
                a: "Yes — up to four Android, iOS or Windows devices can share simultaneously, with no extra hardware.",
            },
            {
                q: "What warranty and service do we get?",
                a: "A 3-year on-site warranty (extendable) backed by our pan-India service network, plus on-site teacher training at installation.",
            },
            {
                q: "Can we record lessons on the panel?",
                a: "Yes. Lessons and annotations record on the device and upload to the cloud, so absent students and remote participants never miss a session.",
            },
        ],
    },
    {
        slug: "solar-system",
        name: "Solar System",
        tagline: "Clean energy, engineered end to end.",
        positioning:
            "Rooftop and commercial solar — designed, installed, approved and monitored by one team.",
        overview:
            "From a 1 kW home rooftop to 1 MW+ commercial plants, we handle the entire solar journey: site survey, system design, DISCOM approvals and net metering, installation, commissioning and long-term maintenance. High-efficiency TOPCon panels, hybrid-ready inverters and a monitoring app mean you watch every unit — and every rupee — from day one.",
        image: "/assets/product-solar.jpeg",
        imageAlt: "Rooftop solar panel installation under a bright sky",
        icon: Sun,
        glance: [
            { value: "1 kW–1 MW+", label: "Capacity range" },
            { value: "4–5", label: "Units per kW / day" },
            { value: "21–22%", label: "Panel efficiency" },
            { value: "25 yrs", label: "Performance warranty" },
            { value: "Rs 78,000", label: "Max subsidy assistance" },
        ],
        features: [
            {
                icon: Sun,
                title: "High-efficiency TOPCon panels",
                text: "Monocrystalline half-cut modules at 540–580 Wp with 21–22% efficiency, IEC and BIS certified.",
            },
            {
                icon: PlugZap,
                title: "On-grid, off-grid & hybrid",
                text: "String inverters from 3–100 kW at up to 98.5% efficiency, net-metering compliant.",
            },
            {
                icon: BatteryCharging,
                title: "LiFePO4 storage",
                text: "5–20 kWh batteries with 6000+ cycles and a 10-year warranty keep hybrid sites running through outages.",
            },
            {
                icon: Activity,
                title: "Live monitoring app",
                text: "Generation, savings and CO₂ tracking on your phone, with remote fault alerts to our O&M team.",
            },
            {
                icon: Wind,
                title: "150 km/h wind-rated structures",
                text: "GI or aluminium mounting, elevated options, corrosion resistant, with a 10-year structural warranty.",
            },
            {
                icon: BadgeCheck,
                title: "Subsidy & net metering handled",
                text: "PM Surya Ghar subsidy assistance, MNRE-approved vendor, and all DISCOM paperwork done for you.",
            },
        ],
        benefits: [
            { stat: "4–5", text: "units generated per kW per day in Indian conditions" },
            { stat: "80%", text: "output warranted at 25 years — in writing" },
            { stat: "Rs 78,000", text: "residential subsidy assistance under PM Surya Ghar" },
            { stat: "≤0.5%", text: "annual degradation keeps savings compounding for decades" },
        ],
        audiences: [
            {
                icon: Home,
                title: "Homes & housing societies",
                text: "3–10 kW rooftops with subsidy assistance and EMI options.",
            },
            {
                icon: GraduationCap,
                title: "Schools & institutions",
                text: "Cut operating costs and turn the roof into a living science lesson.",
            },
            {
                icon: Factory,
                title: "Factories & warehouses",
                text: "High-consumption sites offset peak tariffs and claim 80% accelerated depreciation.",
            },
            {
                icon: Building,
                title: "Commercial buildings",
                text: "Lease and EMI structures that make the system cash-flow positive early.",
            },
        ],
        specGroups: [
            {
                title: "Capacity",
                rows: [
                    ["Range", "1 kW residential to 1 MW+ commercial"],
                    ["Typical rooftops", "3 kW, 5 kW and 10 kW systems"],
                ],
            },
            {
                title: "Panels",
                rows: [
                    ["Type", "Monocrystalline half-cut PERC/TOPCon"],
                    ["Output", "540–580 Wp per module; 21–22% efficiency"],
                    ["Warranty", "25-year performance warranty (at least 80% output at 25 years); 12-year product warranty"],
                    ["Certification", "IEC 61215 and 61730 certified; ALMM/BIS approved"],
                ],
            },
            {
                title: "Inverter",
                rows: [
                    ["Types", "On-grid, off-grid or hybrid string inverters, 3–100 kW"],
                    ["Efficiency", "97–98.5%; 2× MPPT"],
                    ["Protection", "IP65; net-metering and anti-islanding compliant"],
                    ["Warranty", "5–10 year warranty; remote monitoring app"],
                ],
            },
            {
                title: "Battery (Hybrid / Off-grid)",
                rows: [
                    ["Chemistry", "LiFePO4, 5–20 kWh"],
                    ["Lifecycle", "6000+ cycles; 90% depth of discharge"],
                    ["Warranty", "10-year warranty"],
                ],
            },
            {
                title: "Mounting Structure",
                rows: [
                    ["Material", "GI or aluminium; elevated and super-elevated options"],
                    ["Rating", "Wind rated to 150 km/h; corrosion resistant"],
                    ["Warranty", "10-year structural warranty"],
                ],
            },
            {
                title: "Balance of System",
                rows: [
                    ["Cabling", "Solar-grade DC/AC cables; MC4 connectors"],
                    ["Protection", "ACDB/DCDB, lightning arrestor, earthing, IP65 junction boxes"],
                ],
            },
            {
                title: "Performance",
                rows: [
                    ["Generation", "4–5 units generated per kW per day (India conditions)"],
                    ["Quality", "Performance ratio 78–82%; degradation at or under 0.5% per year"],
                    ["System life", "25 years"],
                ],
            },
            {
                title: "Monitoring",
                rows: [
                    ["Connectivity", "Wi-Fi/GSM dongle"],
                    ["App", "Generation, savings and CO₂ tracking; remote fault alerts"],
                ],
            },
            {
                title: "Subsidy & Finance",
                rows: [
                    ["Subsidy", "PM Surya Ghar subsidy assistance (up to Rs 78,000 residential)"],
                    ["Approvals", "MNRE-approved vendor; net-metering documentation handled"],
                    ["Finance", "EMI and lease options; 80% accelerated depreciation for businesses"],
                ],
            },
            {
                title: "Scope of Work",
                rows: [
                    ["Included", "Site survey, load study, system design and SLD, DISCOM approvals and net metering, installation, commissioning"],
                    ["O&M", "5-year O&M AMC"],
                ],
            },
        ],
        delivery: [
            { step: "Survey", text: "Site survey and load study size the system to your bills." },
            { step: "Design", text: "System design, SLD, DISCOM approvals and net metering — handled." },
            { step: "Install", text: "Installation and commissioning by certified crews." },
            { step: "Maintain", text: "Remote monitoring plus a 5-year O&M AMC." },
        ],
        faqs: [
            {
                q: "How much will we actually save?",
                a: "In Indian conditions a well-designed system generates 4–5 units per kW per day. Multiply that by your tariff — most rooftop customers see the majority of their grid bill disappear.",
            },
            {
                q: "Is the government subsidy real, and who does the paperwork?",
                a: "Yes. Residential systems qualify for PM Surya Ghar subsidy assistance of up to Rs 78,000. As an MNRE-approved vendor we handle the application and net-metering documentation end to end.",
            },
            {
                q: "On-grid, off-grid or hybrid — what's the difference?",
                a: "On-grid feeds the grid and earns net-metering credits; off-grid runs on batteries alone; hybrid does both. We recommend the right architecture after the load study.",
            },
            {
                q: "What happens during monsoon or winter?",
                a: "Generation dips seasonally but the system is designed around annual yield — our 78–82% performance ratio figures already account for Indian weather patterns.",
            },
            {
                q: "What maintenance does a solar plant need?",
                a: "Very little: periodic cleaning and inspection. Our 5-year O&M AMC covers it, and the monitoring app alerts our team to faults before you notice them.",
            },
        ],
    },
];

export const getProduct = (slug) => PRODUCTS.find((p) => p.slug === slug);
